import {useState, useEffect, useCallback, useMemo, useRef} from 'react'
import {useAuth} from '../../../../auth'
import {useGlobalFilter} from '../../../../../shared/context/GlobalFilterContext'
import {fetchAccessibleVesselsForCurrentUser} from '../../../../../shared/access/accessibleVessels'
import {searchDefects, searchNearMissReports, searchAudits, searchInspections, searchWorkPermits,
  searchAuditFindings, searchInspectionFindings, listRiskLibraryRiskNumberOptions} from '../../../core/_requests'
import {QHSE_PERMS} from '../../../core/permissions'
import {allPages, loadCertificates, kpiRecords, newestFirst, rowsFromResponse} from '../dashboardData'
import type {QhseDashboardData, QhseDerivedKpis, SourceErrors, SourceKey} from '../types'

const emptyData = (): QhseDashboardData => ({defects: [], nearMisses: [], audits: [], inspections: [], workPermits: [],
  vesselCertificates: [], officeCertificates: [], auditFindings: [], inspectionFindings: [], riskTasks: []})

export function useQhseDashboard() {
  const {currentUser, permissionsLoaded, can} = useAuth()
  const {state} = useGlobalFilter()
  const companyId = state.company?.id
  const companyType = state.company?.type
  const vesselId = state.vessel?.id
  const [loading, setLoading] = useState(true)
  const [sourceData, setData] = useState<QhseDashboardData>(emptyData)
  const [errors, setErrors] = useState<SourceErrors>({})
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const generation = useRef(0)
  const activeRequest = useRef<number | null>(null)

  const refresh = useCallback(async (background = false) => {
    if (background && activeRequest.current != null) return
    const request = ++generation.current
    if (!background) {
      setLoading(true)
      setData(emptyData())
      setErrors({})
      setLastUpdated(null)
    }
    if (!permissionsLoaded || !currentUser) return
    activeRequest.current = request
    const next = emptyData()
    const failures: SourceErrors = {}
    const params = {activeOnly: true,
      ...(companyId ? companyType === 'company_admin' ? {companyId} : {companyGroupId: companyId} : {}),
      ...(vesselId ? {vesselId: Number(vesselId)} : {})}
    let vessels: Awaited<ReturnType<typeof fetchAccessibleVesselsForCurrentUser>> = []
    let scopeError = false
    try {
      vessels = (await fetchAccessibleVesselsForCurrentUser()).filter(v =>
        (!companyId || Number(companyType === 'company_admin' ? v.companyAdminId : v.companyGroupAdminId) === companyId) &&
        (!vesselId || Number(v.id) === Number(vesselId)))
    } catch { scopeError = true }
    const ids = new Set(vessels.map(v => Number(v.id)))
    const scoped = <T extends {vesselId?: number | null; active?: boolean | null}>(rows: T[]) =>
      rows.filter(row => ids.has(Number(row.vesselId)) && row.active !== false)
    const jobs: [SourceKey, string, () => Promise<unknown>][] = [
      ['defects', QHSE_PERMS.DEFECT_READ, () => searchDefects(params)],
      ['nearMisses', QHSE_PERMS.NEAR_MISS_READ, () => searchNearMissReports(params)],
      ['audits', QHSE_PERMS.AUDIT_READ, () => allPages(page => searchAudits({...params, page, size: 200}))],
      ['inspections', QHSE_PERMS.INSPECTION_READ, () => allPages(page => searchInspections({...params, page, size: 200}))],
      ['workPermits', QHSE_PERMS.WORK_PERMIT_READ, () => searchWorkPermits(params)],
      ['vesselCertificates', QHSE_PERMS.CERT_READ, () => loadCertificates('Vessel', Array.from(ids))],
      ['officeCertificates', QHSE_PERMS.OFFICE_CERT_READ, () => loadCertificates('Office', Array.from(ids))],
      ['auditFindings', QHSE_PERMS.AUDIT_FINDING_READ, () => searchAuditFindings(params)],
      ['inspectionFindings', QHSE_PERMS.INSPECTION_FINDING_READ, () => searchInspectionFindings(params)],
      ['riskTasks', QHSE_PERMS.RISK_LIB_READ, async () => {
        // Use the library's own vessel visibility and override rules; count shared records once.
        const lists = await Promise.all(vessels.map(v => listRiskLibraryRiskNumberOptions(false,
          v.companyGroupAdminId ?? undefined, Number(v.id))))
        return Array.from(new Map(lists.flat().map(task => [task.libraryTaskId, task])).values())
      }],
    ]
    await Promise.all(jobs.map(async ([key, permission, load]) => {
      if (!can(permission)) { failures[key] = 'No access'; return }
      if (scopeError) { failures[key] = 'Unable to load vessel access'; return }
      try {
        const rows = rowsFromResponse<any>(ids.size ? await load() : [])
        ;(next[key] as unknown[]) = key === 'riskTasks' ? rows : scoped(rows)
        if (key === 'vesselCertificates' || key === 'officeCertificates') {
          next[key].forEach(cert => {cert.vesselName = vessels.find(v => Number(v.id) === Number(cert.vesselId))?.name})
        }
      } catch { failures[key] = 'Failed to load — use Refresh to retry' }
    }))
    if (activeRequest.current === request) activeRequest.current = null
    if (request !== generation.current) return
    next.defects = newestFirst(next.defects, d => d.dateObserved)
    next.nearMisses = newestFirst(next.nearMisses, d => d.dateOfOccurrence || d.dateIssued || d.createdAt)
    next.workPermits = newestFirst(next.workPermits, d => d.startDate || d.createdAt)
    setData(next)
    setErrors(failures)
    setLastUpdated(new Date())
    setLoading(false)
  }, [currentUser, permissionsLoaded, can, companyId, companyType, vesselId])

  useEffect(() => {
    const requests = generation
    refresh()
    return () => {requests.current++}
  }, [refresh])

  useEffect(() => {
    if (!permissionsLoaded || !currentUser) return
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh(true)
    }, 60000)
    return () => window.clearInterval(timer)
  }, [refresh, permissionsLoaded, currentUser])

  const query = (state.searchQuery || '').trim().toLowerCase()
  const data = useMemo(() => {
    if (!query) return sourceData
    const visible = emptyData()
    for (const key of Object.keys(sourceData) as SourceKey[]) {
      const rows: object[] = sourceData[key]
      ;(visible[key] as unknown[]) = rows.filter(row => Object.values(row).some(value =>
        (typeof value === 'string' && value.toLowerCase().includes(query)) ||
        (Array.isArray(value) && value.some(item => typeof item === 'string' && item.toLowerCase().includes(query)))))
    }
    return visible
  }, [sourceData, query])

  const kpis = useMemo<QhseDerivedKpis>(() => {
    const records = kpiRecords(data)
    return {
      openDefects: errors.defects ? null : records.openDefects.length,
      nearMissesYTD: errors.nearMisses ? null : records.nearMissesYTD.length,
      openAudits: errors.audits ? null : records.openAudits.length,
      openWorkPermits: errors.workPermits ? null : records.openWorkPermits.length,
      pendingInspections: errors.inspections ? null : records.pendingInspections.length,
      expiringCerts: errors.vesselCertificates || errors.officeCertificates ? null : records.expiringCerts.length,
    }
  }, [data, errors])

  return {loading, data, errors, lastUpdated, kpis, refresh}
}
